import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Button } from './ui/button';
// import type { GameRole } from '@/types/game';
import { teams } from '@/config/teams';
// import { LanguageSelector } from './LanguageSelector';
import { generateUserId, cn } from '@/lib/utils';

interface SessionLinksPageProps {
  sessionId: string;
  onNewSession: () => void;
}

interface SessionLink {
  title: string;
  description: string;
  url: string;
  type: 'master' | 'team';
}

export function SessionLinksPage({ sessionId, onNewSession }: SessionLinksPageProps) {
  const { t } = useTranslation();
  const baseUrl = `${window.location.origin}${import.meta.env.PROD ? '/storm' : ''}`;
  const [copiedStates, setCopiedStates] = useState<boolean[]>([]);

  const generateSessionLinks = (): SessionLink[] => {
    // Master link
    const masterLink: SessionLink = {
      title: t('game.roles.master'),
      description: t('game.roles.masterDescription'),
      url: `${baseUrl}?role=master&session=${sessionId}&userId=${generateUserId()}&userName=${encodeURIComponent(t('game.roles.master'))}`,
      type: 'master'
    };

    // Team links
    const teamLinks: SessionLink[] = teams.map((team) => {
      const userId = generateUserId();
      const teamName = t(team.name);
      return {
        title: teamName,
        description: t('game.roles.teamDescription'),
        url: `${baseUrl}?role=team&team=${team.id}&session=${sessionId}&userId=${userId}&userName=${encodeURIComponent(teamName)}`,
        type: 'team'
      };
    });

    return [masterLink, ...teamLinks];
  };

  const links = generateSessionLinks();

  const copyToClipboard = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      const newCopiedStates = [...copiedStates];
      newCopiedStates[index] = true;
      setCopiedStates(newCopiedStates);
      setTimeout(() => {
        const resetStates = [...newCopiedStates];
        resetStates[index] = false;
        setCopiedStates(resetStates);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 p-4">
      <div className="w-full max-w-4xl space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {t('game.title')}
          </h1>
          <h2 className="text-2xl font-semibold mb-2">
            {t('game.session.id')}: <span className="font-mono">{sessionId}</span>
          </h2>
          <p className="text-muted-foreground">
            {t('game.session.share')}
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {links.map((link, index) => (
            <Card key={index} className={cn(
              "p-6 border-2 transition-colors",
              link.type === 'master' ? 'hover:border-purple-200' : 'hover:border-blue-200'
            )}>
              <h3 className="text-xl font-semibold mb-2 flex items-center gap-2">
                <div className={cn(
                  "h-2 w-2 rounded-full",
                  link.type === 'master' ? 'bg-purple-500' : 'bg-blue-500'
                )} />
                {link.title}
              </h3>
              <p className="text-muted-foreground mb-6">{link.description}</p>
              <div className="flex gap-2">
                <Button
                  onClick={() => window.open(link.url, '_blank')}
                  className={cn(
                    "flex-1",
                    link.type === 'master' 
                      ? 'bg-gradient-to-r from-purple-600 to-purple-500'
                      : 'bg-gradient-to-r from-blue-600 to-blue-500'
                  )}
                >
                  {t('game.actions.open')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(link.url, index)}
                  className="flex-1"
                >
                  {copiedStates[index]
                    ? t('game.actions.copied')
                    : t('game.actions.copy')}
                </Button>
              </div>
            </Card>
          ))}
        </div>

      </div>
    </div>
  );
}
